import { useEffect, useState } from 'react';

/**
 * Public site/corpus figures from `GET /api/v1/public/stats`.
 *
 * WHY THIS EXISTS (Claim-valid-error-fix-8-19 Q7)
 * ------------------------------------------------
 * The landing page advertised "3,005 FDA Warning Letters" and "12,690+ training
 * records" from three separate hardcoded literals. Nothing counted them, and the
 * real distinct-letter count is an ORDER OF MAGNITUDE below 3,005. One of the
 * three sites read `siteStats.warning_letters_count` off an endpoint that never
 * returned that key, so its fallback rendered unconditionally in production and
 * looked live while being fabricated.
 *
 * The backend now derives these counts from the shipped corpus files (synthetic
 * seed rows excluded), and this hook is the single client-side reader. One
 * fetch site, one number, no literals to drift.
 *
 * ⚠️ Callers MUST render a non-numeric fallback when a value is null. Do not
 * substitute a plausible-looking figure — that is precisely how the last two
 * fabricated numbers survived.
 *
 * Never throws and never rejects: a stats outage must not break the landing page.
 *
 * @returns {{
 *   stats: object|null,
 *   warningLetters: number|null,
 *   totalRecords: number|null,
 *   fmt: (n: number) => string,
 * }}
 */
export default function usePublicStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    fetch(`${apiBase}/api/v1/public/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data && !cancelled) setStats(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return {
    stats,
    warningLetters: stats?.warning_letters_count ?? null,
    totalRecords: stats?.total_records ?? null,
    fmt: (n) => (n >= 1000 ? n.toLocaleString() : `${n}`),
  };
}
